<template>
  <form @submit.prevent="handleSubmit">
    <InputField
      class="pb-[2.4rem] pt-[2.4rem]"
      v-model="email"
      :label="$t('Email *')"
      :placeholder="$t('Email')"
      required
      inputType="email"
      customInputClass="mt-4"
      ref="emailField"
      :inputProps="{ autocomplete: 'email', name: 'email' }"
    />

    <div class="relative pb-[0.8rem]">
      <InputField
        v-model="password"
        :label="$t('Password *')"
        :placeholder="$t('Password')"
        customInputClass="mt-4 pr-[4.8rem]"
        :inputType="showPassword ? 'text' : 'password'"
        required
        ref="passwordField"
        :inputProps="{ autocomplete: 'current-password', name: 'password' }"
      />
      <button
        type="button"
        class="absolute right-[1.6rem] bottom-[2.4rem] flex items-center justify-center text-gray-500 hover:text-black transition-colors"
        :aria-label="showPassword ? $t('Hide password') : $t('Show password')"
        @click="showPassword = !showPassword"
      >
        <svg
          v-if="showPassword"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
    </div>

    <div class="flex justify-end mb-[2rem]">
      <a
        href="#"
        class="text-[1.3rem] text-gray-500 underline hover:text-black transition-colors"
        @click.prevent="showForgotPassword = true"
      >
        {{ $t("Forgot your password?") }}
      </a>
    </div>

    <div v-if="showError" class="text-red-600 text-[1.4rem] mb-4">
      {{ errorMessage }}
    </div>

    <button
      v-if="isLocked"
      type="button"
      disabled
      class="w-full bg-gray-400 text-white text-[1.6rem] font-medium py-[1.4rem] rounded-[10rem] cursor-not-allowed"
    >
      {{ $t("Wait") }} {{ lockoutSeconds }}s
    </button>
    <CustomButton
      v-else
      :label="isSubmitting ? $t('Login') + '...' : $t('Login')"
      type="submit"
    />

    <div class="mt-[2.4rem] pt-[2.4rem] border-t border-gray-200 text-center">
      <p class="text-[1.4rem] md:text-[1.6rem] text-gray-600">
        {{ $t("Don't have an account yet?") }}
        <a
          href="#"
          class="ml-1 font-semibold text-black underline hover:opacity-70 transition-opacity"
          @click.prevent="emit('go-to-register')"
        >
          {{ $t("Sign up here") }}
        </a>
      </p>
    </div>
  </form>

  <CoreModal
    v-model="showForgotPassword"
    :title="$t('Recover your password')"
    :close-on-overlay="true"
    @update:modelValue="onForgotPasswordClose"
  >
    <template #default>
      <div v-if="!forgotPasswordSent">
        <p class="text-[1.4rem] text-gray-600 mb-4">
          {{
            $t(
              "Enter your email and we will send you a link to reset your password.",
            )
          }}
        </p>
        <InputField
          v-model="forgotEmail"
          :label="$t('Email *')"
          :placeholder="$t('Email')"
          required
          inputType="email"
          customInputClass="mt-4"
          ref="forgotEmailField"
        />
        <div v-if="forgotError" class="text-red-600 text-[1.4rem] mt-2">
          {{ forgotError }}
        </div>
      </div>
      <div v-else>
        <p class="text-[1.4rem] text-gray-600">
          {{
            $t(
              "If the email exists, you will receive a link to reset your password shortly.",
            )
          }}
        </p>
      </div>
    </template>
    <template #actions>
      <CustomButton
        v-if="!forgotPasswordSent"
        :label="$t('Send')"
        class="px-[4.8rem] py-[1.8rem] text-[1.8rem] font-medium"
        @click="submitForgotPassword"
      />
    </template>
  </CoreModal>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from "vue";
import type { ComponentPublicInstance } from "vue";
import { useI18n } from "vue-i18n";
import InputField from "@/components/input/inputField.vue";
import CustomButton from "@/components/buttons/customButton.vue";
import CoreModal from "@/components/modal/CoreModal.vue";
import CustomerHelper from "@/helpers/customerHelper";
import { resetPasswordRequest } from "@/services/medkusApi/publiccustomersService";

type InputFieldInstance = ComponentPublicInstance & {
  updateValue: (payload: { target: { value: string } }) => void;
  isInvalid: boolean;
};

const { t } = useI18n();

const emit = defineEmits<{
  (event: "login-success"): void;
  (event: "go-to-register"): void;
}>();

// Form state
const email = ref("");
const password = ref("");
const showPassword = ref(false);
const showError = ref(false);
const errorMessage = ref("");
const isSubmitting = ref(false);

// Lockout state
const failedAttempts = ref(0);
const lockoutSeconds = ref(0);
let lockoutTimer: ReturnType<typeof setInterval> | null = null;

const isLocked = computed(() => lockoutSeconds.value > 0);

// Forgot password state
const showForgotPassword = ref(false);
const forgotEmail = ref("");
const forgotError = ref("");
const forgotPasswordSent = ref(false);

// Template refs
const emailField = ref<InputFieldInstance | null>(null);
const passwordField = ref<InputFieldInstance | null>(null);

const startLockout = (seconds: number) => {
  if (lockoutTimer) clearInterval(lockoutTimer);
  lockoutSeconds.value = seconds;
  lockoutTimer = setInterval(() => {
    lockoutSeconds.value--;
    if (lockoutSeconds.value <= 0) {
      clearInterval(lockoutTimer!);
      lockoutTimer = null;
    }
  }, 1000);
};

onBeforeUnmount(() => {
  if (lockoutTimer) clearInterval(lockoutTimer);
});

const reset = () => {
  email.value = "";
  password.value = "";
  showPassword.value = false;
  showError.value = false;
  errorMessage.value = "";
  isSubmitting.value = false;
  if (lockoutTimer) {
    clearInterval(lockoutTimer);
    lockoutTimer = null;
  }
  lockoutSeconds.value = 0;
  failedAttempts.value = 0;
  showForgotPassword.value = false;
  forgotEmail.value = "";
  forgotError.value = "";
  forgotPasswordSent.value = false;
};

defineExpose({ reset });

const forceValidateFields = () => {
  const payload = (value: string) => ({ target: { value } });
  emailField.value?.updateValue(payload(email.value));
  passwordField.value?.updateValue(payload(password.value));
  return !emailField.value?.isInvalid && !passwordField.value?.isInvalid;
};

const handleSubmit = async () => {
  if (isLocked.value || isSubmitting.value) return;

  showError.value = false;
  errorMessage.value = "";

  if (!forceValidateFields()) return;

  try {
    isSubmitting.value = true;
    const result = await CustomerHelper.loginWithCredentials(
      email.value,
      password.value,
    );

    if (result.success) {
      window.dispatchEvent(new CustomEvent("market-login-success"));
      emit("login-success");
      return;
    }

    if (result.response?.rateLimited === true) {
      failedAttempts.value = 0;
      startLockout(60);
      showError.value = true;
      errorMessage.value = t("Too many attempts. Please wait a moment.");
      return;
    }

    failedAttempts.value++;
    startLockout(failedAttempts.value * 5);
    showError.value = true;
    errorMessage.value = result.message ?? t("User Login failed");
  } catch {
    failedAttempts.value++;
    startLockout(failedAttempts.value * 5);
    showError.value = true;
    errorMessage.value = t("Login failed");
  } finally {
    isSubmitting.value = false;
  }
};

const onForgotPasswordClose = (val: boolean) => {
  if (!val) {
    forgotEmail.value = "";
    forgotError.value = "";
    forgotPasswordSent.value = false;
  }
};

const submitForgotPassword = async () => {
  forgotError.value = "";
  if (!forgotEmail.value || !forgotEmail.value.includes("@")) {
    forgotError.value = t("Please enter a valid email.");
    return;
  }
  try {
    await resetPasswordRequest({ email: forgotEmail.value });
  } catch {
    // Intencionado: no revelar si el email existe o no
  }
  forgotPasswordSent.value = true;
};
</script>
